// --- Core ---
import {Field} from 'react-final-form';
import {FormattedDate, FormattedMessage} from 'react-intl';
import {map, range} from "underscore";
import {addDays, nextMonday, setMonth} from 'date-fns';
// --- Material UI ---
import {MenuItem, Stack} from "@mui/material";
// --- SaniQ ---
import {CheckBoxGroupField, SelectField, TextField, TimeField} from "helpers/Forms";
import {checkBoxGroupAny, composeValidators, dateRequired, regex, required} from "helpers/forms/Validation";
import {DeleteIconButton} from "shared/dialog/Buttons";

interface CronEntryProps {
  name: string,
  period: 'day' | 'week' | 'month' | 'year';
  disableDaily?: boolean;
  onDelete: () => void;
}

const CronEntry = ({name, period, disableDaily, onDelete}: CronEntryProps) => {
  const weekdayNames = map(range(0, 7), day => (
    <FormattedDate key={day} weekday="long" value={addDays(nextMonday(new Date()), day)}/>
  ));

  const monthNames = map(range(0, 12), month => (
    <FormattedDate month="long" value={setMonth(new Date(), month)}/>
  ));

  return (
    <Stack spacing={2} sx={{flexGrow: 1}}>
      <Stack direction="row">
        <Field
          name={`${name}.period`}
          component={SelectField}
          label={<FormattedMessage id="field.cron.entry.period" defaultMessage="Rhythm"/>}
          required validate={required}
        >
          {
            !disableDaily && (
              <MenuItem value={'day'}>
                <FormattedMessage id="field.cron.entry.period.daily" defaultMessage="Daily"/>
              </MenuItem>
            )
          }
          <MenuItem value={'week'}>
            <FormattedMessage id="field.cron.entry.period.weekly" defaultMessage="Weekly"/>
          </MenuItem>
          <MenuItem value={'month'}>
            <FormattedMessage id="field.cron.entry.period.monthly" defaultMessage="Monthly"/>
          </MenuItem>
          <MenuItem value={'year'}>
            <FormattedMessage id="field.cron.entry.period.yearly" defaultMessage="Yearly"/>
          </MenuItem>
        </Field>
        {onDelete && <DeleteIconButton onClick={onDelete}/>}
      </Stack>
      {
        ['week'].includes(period) ?
          <CheckBoxGroupField
            name={`${name}.weekdays`}
            required validate={checkBoxGroupAny}
            label={<FormattedMessage id="field.cron.entry.days_of_week" defaultMessage="Day(s) of week"/>}
            labels={weekdayNames}
          /> : null
      }
      {
        ['month', 'year'].includes(period) ?
          <Field
            name={`${name}.monthDay`}
            component={TextField}
            label={<FormattedMessage id="field.cron.entry.days_of_month" defaultMessage="Day(s) of month"/>}
            customHelperText={
              <FormattedMessage
                id="field.cron.entry.days_of_month.helper"
                // eslint-disable-next-line @stylistic/max-len
                defaultMessage="Please input the desired days separated by comma. Please consider that days 29, 30 and 31 do not exist for every month."
              />
            }
            required
            validate={
              composeValidators(required, regex(/^\s*([1-9]|[1-2]\d|3[0-1])(\s*,\s*([1-9]|[1-2]\d|3[0-1])){0,30}\s*$/,
                <FormattedMessage
                  id="field.cron.entry.days_of_month.error"
                  defaultMessage="Must be a list of days separated by comma. Example: 1, 15"
                />
              ))
            }
          /> : null
      }
      {
        ['year'].includes(period) ?
          <CheckBoxGroupField
            name={`${name}.months`}
            required validate={checkBoxGroupAny}
            label={<FormattedMessage id="field.cron.entry.months" defaultMessage="Month(s)"/>}
            labels={monthNames}
          /> : null
      }
      <Field
        name={`${name}.time`}
        component={TimeField}
        required validate={dateRequired}
        label={<FormattedMessage id="field.cron.entry.time" defaultMessage="Time"/>}
      />
    </Stack>
  );
};

export default CronEntry;
